/**
 * The per-type spec table — every built-in render plugin's canonical
 * `(id, role)` and the per-type metadata every render front-end reads (the
 * DOM, the CLI and the TUI all map back to it), in one module: one
 * `PerTypeSpec` const per plugin id, grouped by role, then the role lists and
 * `PER_TYPE_SPECS`, which the render-plugin catalog
 * (`../render/render-plugin-catalog.ts`) builds `BUILTIN_RENDER_PLUGIN_SPECS`
 * from.
 *
 * Order is part of the contract: the catalog is composed from these lists,
 * and the cross-platform parity test asserts that platform manifests keep
 * this ordering. A new per-type spec goes in the role section it belongs to
 * and in its role list; do not append to the bottom.
 */

import { calendarListDataProvider } from "../providers/calendar-list-events.ts";
import { calendarAllDayDataProvider } from "../providers/calendar-allday-events.ts";
import { RowShapes } from "../rows/row-shapes.ts";
import { tasksDataProvider } from "../providers/tasks-events.ts";
import { calendarTimedEventsDataProvider } from "../providers/calendar-timed-events.ts";
import type { PerTypeSpec } from "./per-type-spec.ts";
import { deepFrozen } from "../core/frozen-collections.ts";

export type { PerTypeSpec } from "./per-type-spec.ts";
export type { PerTypeAdapter } from "./per-type-adapter.ts";

// ── Alias rows ────────────────────────────────────────────────────────

/**
 * The `alias.calendar` alias row's per-type spec, listed in `ALIAS_PER_TYPE_SPECS`; each render
 * front-end's plugin for that id implements it.
 */
export const CALENDAR_ALIAS_SPEC: PerTypeSpec = {
  id: "alias.calendar",
  role: "alias",
  dataProvider: calendarListDataProvider,
};

/**
 * The `alias.calendar-allday` alias row's per-type spec, listed in `ALIAS_PER_TYPE_SPECS`; each
 * render front-end's plugin for that id implements it.
 */
export const CALENDAR_ALLDAY_ALIAS_SPEC: PerTypeSpec = {
  id: "alias.calendar-allday",
  role: "alias",
  dataProvider: calendarAllDayDataProvider,
};

/**
 * The `alias.calendar-list` alias row's per-type spec, listed in `ALIAS_PER_TYPE_SPECS`; each
 * render front-end's plugin for that id implements it.
 */
export const CALENDAR_LIST_ALIAS_SPEC: PerTypeSpec = {
  id: "alias.calendar-list",
  role: "alias",
  dataProvider: calendarListDataProvider,
};

/**
 * The `alias.chart` alias row's per-type spec, listed in `ALIAS_PER_TYPE_SPECS`; each render
 * front-end's plugin for that id implements it.
 */
export const CHART_ALIAS_SPEC: PerTypeSpec = {
  id: "alias.chart",
  role: "alias",
};

/**
 * The data alias row's per-type spec (`alias.data`, spelled as every alias
 * spec is), listed in `ALIAS_PER_TYPE_SPECS`; each render front-end's plugin
 * for that id implements it. `data` — the template a stored `data` row
 * hydrates with — is its alias.
 */
export const DATA_ALIAS_SPEC: PerTypeSpec = {
  id: "alias.data",
  role: "alias",
  aliases: ["data"],
};

/**
 * `alias.stickers` — a thin subcolumn-only wrapper around the canonical
 * `:~lona:stickers` sticker source row, so stickers can overlay the calendar's
 * timed grid alongside events.
 *
 * It has no `dataProvider` and no `contentProviders` of its own: subcolumn
 * discovery (`collectCalendarSubcolumns`) descends THROUGH this alias to the
 * inner `sticker` source row, whose `StickerSourcePlugin.contentProviders.calendar`
 * supplies the (zero-lane, `stagger: false`) overlay subcolumn. Because the
 * calendar's cell-bindable descendant is the `timeline[column]` layout — not
 * this alias — neither the alias nor its sticker child renders as a visible row.
 */
export const STICKERS_ALIAS_SPEC: PerTypeSpec = {
  id: "alias.stickers",
  role: "alias",
};

/**
 * The `alias.task` alias row's per-type spec, listed in `ALIAS_PER_TYPE_SPECS`; each render
 * front-end's plugin for that id implements it.
 */
export const TASK_ALIAS_SPEC: PerTypeSpec = {
  id: "alias.task",
  role: "alias",
};

/**
 * The `alias.task-list` alias row's per-type spec, listed in `ALIAS_PER_TYPE_SPECS`; each render
 * front-end's plugin for that id implements it.
 */
export const TASK_LIST_ALIAS_SPEC: PerTypeSpec = {
  id: "alias.task-list",
  role: "alias",
};

/**
 * The `alias.timezone` alias row's per-type spec, listed in `ALIAS_PER_TYPE_SPECS`; each render
 * front-end's plugin for that id implements it.
 */
export const TIMEZONE_ALIAS_SPEC: PerTypeSpec = {
  id: "alias.timezone",
  role: "alias",
};

/**
 * The `alias.weather` alias row's per-type spec, listed in `ALIAS_PER_TYPE_SPECS`; each render
 * front-end's plugin for that id implements it.
 */
export const WEATHER_ALIAS_SPEC: PerTypeSpec = {
  id: "alias.weather",
  role: "alias",
  // The user's default-weather row is an `alias.default-weather` alias
  // (preference-driven location). It renders with the weather chrome, so the
  // weather plugin answers that template id too. (`aliases` = a plugin serving
  // a second *current* template id — distinct from the retired `legacyIds`.)
  aliases: ["alias.default-weather"],
};

// ── Layout rows ───────────────────────────────────────────────────────

/**
 * The `canvas` layout row's per-type spec, listed in `LAYOUT_PER_TYPE_SPECS`; each render
 * front-end's plugin for that id implements it.
 */
export const CANVAS_LAYOUT_SPEC: PerTypeSpec = {
  id: "canvas",
  role: "layout",
};

/**
 * The `column` layout row's per-type spec, listed in `LAYOUT_PER_TYPE_SPECS`; each render
 * front-end's plugin for that id implements it.
 */
export const COLUMN_LAYOUT_SPEC: PerTypeSpec = {
  id: "column",
  role: "layout",
};

/**
 * The `grid` layout row's per-type spec, listed in `LAYOUT_PER_TYPE_SPECS`; each render front-end's
 * plugin for that id implements it.
 */
export const GRID_LAYOUT_SPEC: PerTypeSpec = {
  id: "grid",
  role: "layout",
};

/**
 * `calendar-body` — the calendar's body layout (row type `calendar-body`,
 * `CalendarBodyRow`): it hosts a set of CANDIDATE layouts and delegates to
 * whichever one the row's declarative {@link SwitchConfig} selects for the
 * current sheet time scale (`grid` at month, `timeline[column]` otherwise).
 * `switch`, the id it was registered under before, stays an alias, so a
 * stored plugin id still resolves.
 */
export const CALENDAR_BODY_LAYOUT_SPEC: PerTypeSpec = {
  id: "calendar-body",
  role: "layout",
  aliases: ["switch"],
};

/**
 * `timeline[column]` — the clean, non-switching timed grid leaf (hourly grid,
 * current-time marker, vertical-axis event positioning). The calendar body's
 * `calendar-body` type now resolves to the generic `switch` layout
 * ({@link CALENDAR_BODY_LAYOUT_SPEC}), which delegates to this plugin for week/day and
 * to `grid` for month — so this stays a pure timed primitive, usable standalone.
 */
export const TIMELINE_COLUMN_LAYOUT_SPEC: PerTypeSpec = {
  id: "timeline[column]",
  role: "layout",
};

/**
 * The all-day events strip in `calendar-allday` paints under a
 * `timeline[row]` layout — its renderer row collapses under the
 * layout in the rendered projection, so the layout needs the
 * all-day provider to surface fixed-child intent. The provider
 * returns empty for any `timeline[row]` subtree without a
 * calendar-list aggregate, so this wiring is safe for non-calendar
 * timelines.
 */
export const TIMELINE_ROW_LAYOUT_SPEC: PerTypeSpec = {
  id: "timeline[row]",
  role: "layout",
  // Band rows arrive under `timeline[row]` (the calendar template's
  // projection child + the standalone `alias.calendar-allday` strip
  // child) plus legacy bare `timeline`. The alias keys this plugin's
  // registration — and its descriptor override — to both.
  aliases: ["timeline"],
  dataProvider: calendarAllDayDataProvider,
};

// ── Inert (display-only) rows ─────────────────────────────────────────

/**
 * The `block` inert row's per-type spec, listed in `INERT_PER_TYPE_SPECS`; each render front-end's
 * plugin for that id implements it.
 */
export const BLOCK_INERT_SPEC: PerTypeSpec = deepFrozen({
  id: "block",
  role: "inert",
});

/**
 * The `group` inert row's per-type spec, listed in `INERT_PER_TYPE_SPECS`; each render front-end's
 * plugin for that id implements it.
 */
export const GROUP_INERT_SPEC: PerTypeSpec = {
  id: "group",
  role: "inert",
};

/**
 * The `header` inert row's per-type spec, listed in `INERT_PER_TYPE_SPECS`; each render front-end's
 * plugin for that id implements it.
 */
export const HEADER_INERT_SPEC: PerTypeSpec = {
  id: "header",
  role: "inert",
};

/**
 * The `label` inert row's per-type spec, listed in `INERT_PER_TYPE_SPECS`; each render front-end's
 * plugin for that id implements it.
 */
export const LABEL_INERT_SPEC: PerTypeSpec = {
  id: "label",
  role: "inert",
};

/**
 * The `sheet-footer` inert row's per-type spec, listed in `INERT_PER_TYPE_SPECS`; each render
 * front-end's plugin for that id implements it.
 */
export const SHEET_FOOTER_INERT_SPEC: PerTypeSpec = {
  id: "sheet-footer",
  role: "inert",
};

/**
 * The `surface` inert row's per-type spec, listed in `INERT_PER_TYPE_SPECS`; each render
 * front-end's plugin for that id implements it.
 */
export const SURFACE_INERT_SPEC: PerTypeSpec = {
  id: "surface",
  role: "inert",
};

/**
 * The `timeline-legend` inert row's per-type spec, listed in `INERT_PER_TYPE_SPECS`; each render
 * front-end's plugin for that id implements it.
 */
export const TIMELINE_LEGEND_INERT_SPEC: PerTypeSpec = {
  id: "timeline-legend",
  role: "inert",
};

// ── Renderers ─────────────────────────────────────────────────────────

/**
 * The `canvas.bar` renderer's per-type spec, listed in `RENDERER_PER_TYPE_SPECS`; each render
 * front-end's plugin for that id implements it.
 */
export const CANVAS_BAR_SPEC: PerTypeSpec = {
  id: "canvas.bar",
  role: "renderer",
};

/**
 * Stub canonical entry: a dedicated candlestick renderer hasn't shipped
 * yet; both adapters register a stub plugin so the canonical id is
 * resolvable and the catalog/DOM parity tests stay green. Rendering
 * inherits the shared canvas series path.
 */
export const CANVAS_CANDLESTICK_SPEC: PerTypeSpec = {
  id: "canvas.candlestick",
  role: "renderer",
};

/**
 * The `canvas.funnel` renderer's per-type spec, listed in `RENDERER_PER_TYPE_SPECS`; each render
 * front-end's plugin for that id implements it.
 */
export const CANVAS_FUNNEL_SPEC: PerTypeSpec = {
  id: "canvas.funnel",
  role: "renderer",
};

/** The per-type spec of the canvas horizontal-rule renderer. */
export const CANVAS_HRULE_SPEC: PerTypeSpec = {
  id: "canvas.hrule",
  role: "renderer",
};

/**
 * The `canvas.line` renderer's per-type spec, listed in `RENDERER_PER_TYPE_SPECS`; each render
 * front-end's plugin for that id implements it.
 */
export const CANVAS_LINE_SPEC: PerTypeSpec = {
  id: "canvas.line",
  role: "renderer",
};

/**
 * The `canvas.points` renderer's per-type spec, listed in `RENDERER_PER_TYPE_SPECS`; each render
 * front-end's plugin for that id implements it.
 */
export const CANVAS_POINTS_SPEC: PerTypeSpec = {
  id: "canvas.points",
  role: "renderer",
};

/**
 * The `canvas.regression-band` renderer's per-type spec, listed in `RENDERER_PER_TYPE_SPECS`; each
 * render front-end's plugin for that id implements it.
 */
export const CANVAS_REGRESSION_BAND_SPEC: PerTypeSpec = {
  id: "canvas.regression-band",
  role: "renderer",
};

/**
 * The `canvas.regression-curve` renderer's per-type spec, listed in `RENDERER_PER_TYPE_SPECS`; each
 * render front-end's plugin for that id implements it.
 */
export const CANVAS_REGRESSION_CURVE_SPEC: PerTypeSpec = {
  id: "canvas.regression-curve",
  role: "renderer",
};

/**
 * The `canvas.regression-fill` renderer's per-type spec, listed in `RENDERER_PER_TYPE_SPECS`; each
 * render front-end's plugin for that id implements it.
 */
export const CANVAS_REGRESSION_FILL_SPEC: PerTypeSpec = {
  id: "canvas.regression-fill",
  role: "renderer",
};

/**
 * The `column.checkmark` renderer's per-type spec, listed in `RENDERER_PER_TYPE_SPECS`; each render
 * front-end's plugin for that id implements it.
 */
export const COLUMN_CHECKMARK_SPEC: PerTypeSpec = {
  id: "column.checkmark",
  role: "renderer",
};

/**
 * The `column.data-json` renderer's per-type spec, listed in `RENDERER_PER_TYPE_SPECS`; each render
 * front-end's plugin for that id implements it.
 */
export const COLUMN_DATA_JSON_SPEC: PerTypeSpec = {
  id: "column.data-json",
  role: "renderer",
};

/**
 * `column.event-list` — `column`-slot renderer for event-bearing
 * rows. Paints the column's events as a flat vertical list
 * (rowkey-architecture.md §Renderer lookup: obj/series/Event →
 * `column.event-list`). Declared by the calendar-list alias
 * template so plain column layouts render events as a flat list
 * instead of falling through to the data-json dump.
 */
export const COLUMN_EVENT_LIST_SPEC: PerTypeSpec = {
  id: "column.event-list",
  role: "renderer",
};

/**
 * The `column.graph` renderer's per-type spec, listed in `RENDERER_PER_TYPE_SPECS`; each render
 * front-end's plugin for that id implements it.
 */
export const COLUMN_GRAPH_SPEC: PerTypeSpec = {
  id: "column.graph",
  role: "renderer",
};

/**
 * The `column.header-secondary` renderer's per-type spec, listed in `RENDERER_PER_TYPE_SPECS`; each
 * render front-end's plugin for that id implements it.
 */
export const COLUMN_HEADER_SECONDARY_SPEC: PerTypeSpec = {
  id: "column.header-secondary",
  role: "renderer",
};

/**
 * Stub canonical entry: a dedicated heatmap renderer hasn't shipped
 * yet; both adapters register a stub plugin so the canonical id is
 * resolvable. DOM falls back to `column.data-json` rendering.
 */
export const COLUMN_HEATMAP_SPEC: PerTypeSpec = {
  id: "column.heatmap",
  role: "renderer",
};

/**
 * The `column.logs` renderer's per-type spec, listed in `RENDERER_PER_TYPE_SPECS`; each render
 * front-end's plugin for that id implements it.
 */
export const COLUMN_LOGS_SPEC: PerTypeSpec = {
  id: "column.logs",
  role: "renderer",
  accepts: [{ shape: RowShapes.LOGS.key, onMismatch: "ignore" }],
};

/**
 * The `column.number` renderer's per-type spec, listed in `RENDERER_PER_TYPE_SPECS`; each render
 * front-end's plugin for that id implements it.
 */
export const COLUMN_NUMBER_SPEC: PerTypeSpec = {
  id: "column.number",
  role: "renderer",
  /**
   * Number cells render via `String(value)`. CLI snapshots will
   * catch any DOM-side locale formatting that diverges (e.g., DOM
   * uses `toLocaleString` here, CLI doesn't). When a DOM-side
   * locale formatter lands, mirror it in this `cellValueFormatter`
   * so CLI catches future drift.
   */
  cellValueFormatter: (value) =>
    typeof value === "number" ? String(value) : "",
};

/**
 * The `column.series-summary` renderer's per-type spec, listed in `RENDERER_PER_TYPE_SPECS`; each
 * render front-end's plugin for that id implements it.
 */
export const COLUMN_SERIES_SUMMARY_SPEC: PerTypeSpec = {
  id: "column.series-summary",
  role: "renderer",
};

/**
 * `column.sticker` — default `column`-slot renderer for sticker
 * source rows. Paints the row's sticker custom cells (and legacy
 * legacy plain-cell sticker arrays) as a vertical sticker list per
 * column (rowkey-architecture.md §Renderer lookup: obj/scalar/
 * Sticker → `column.sticker`).
 */
export const COLUMN_STICKER_SPEC: PerTypeSpec = {
  id: "column.sticker",
  role: "renderer",
};

/**
 * The `column.task` renderer's per-type spec, listed in `RENDERER_PER_TYPE_SPECS`; each render
 * front-end's plugin for that id implements it.
 */
export const COLUMN_TASK_SPEC: PerTypeSpec = {
  id: "column.task",
  role: "renderer",
  accepts: [{ shape: RowShapes.TASK.key, onMismatch: "ignore" }],
  dataProvider: tasksDataProvider,
};

/**
 * The `column.text` renderer's per-type spec, listed in `RENDERER_PER_TYPE_SPECS`; each render
 * front-end's plugin for that id implements it.
 */
export const COLUMN_TEXT_SPEC: PerTypeSpec = {
  id: "column.text",
  role: "renderer",
  /**
   * Text cells render the raw string value. CLI snapshots assert
   * this matches DOM rendering — so any DOM-side normalization
   * (e.g., trim, max-length) needs to apply here too.
   */
  cellValueFormatter: (value) =>
    typeof value === "string" ? value : "",
};

/**
 * The `column.timezone` renderer's per-type spec, listed in `RENDERER_PER_TYPE_SPECS`; each render
 * front-end's plugin for that id implements it.
 */
export const COLUMN_TIMEZONE_SPEC: PerTypeSpec = {
  id: "column.timezone",
  role: "renderer",
  accepts: [{ shape: RowShapes.TIMEZONE.key, onMismatch: "ignore" }],
};

/**
 * `column.video` — `column`-slot renderer for video source rows.
 * Paints the column's cell as an embedded YouTube playlist (thumbnail
 * + play button, expanding to an inline player on click). Declared via
 * `rendererOverride` on the `video` source descriptor (raw-sheet.ts) so
 * video rows render the player instead of dumping their stored URL JSON
 * as plain text. The DOM implementation is `ColumnVideoRendererPlugin`
 * (sheets-ui/plugins/renderer/column-video.ts); no rows-side renderer
 * class is needed — unmapped ids round-trip through the generic
 * `RendererRow` (hydrate-default-renderer.ts).
 */
export const COLUMN_VIDEO_SPEC: PerTypeSpec = {
  id: "column.video",
  role: "renderer",
};

/**
 * The `column.weather` renderer's per-type spec, listed in `RENDERER_PER_TYPE_SPECS`; each render
 * front-end's plugin for that id implements it.
 */
export const COLUMN_WEATHER_SPEC: PerTypeSpec = {
  id: "column.weather",
  role: "renderer",
  accepts: [{ shape: RowShapes.WEATHER.key, onMismatch: "ignore" }],
};

/**
 * `grid.event` paints every event the calendar-list aggregate
 * carries (partial- and full-day) in a month-grid layout.
 */
export const GRID_EVENT_SPEC: PerTypeSpec = {
  id: "grid.event",
  role: "renderer",
  accepts: [{ shape: RowShapes.EVENT.key, onMismatch: "ignore" }],
  dataProvider: calendarListDataProvider,
};

/**
 * The `grid.task` renderer's per-type spec, listed in `RENDERER_PER_TYPE_SPECS`; each render
 * front-end's plugin for that id implements it.
 */
export const GRID_TASK_SPEC: PerTypeSpec = {
  id: "grid.task",
  role: "renderer",
  accepts: [{ shape: RowShapes.TASK.key, onMismatch: "ignore" }],
  dataProvider: tasksDataProvider,
};

/**
 * Timed grid — filters to non-all-day events only. All-day events
 * live on the `timeline[row].event` strip; routing them through both
 * surfaces would double-paint them.
 */
export const TIMELINE_COLUMN_EVENT_SPEC: PerTypeSpec = {
  id: "timeline[column].event",
  role: "renderer",
  accepts: [{ shape: RowShapes.EVENT.key, onMismatch: "ignore" }],
  dataProvider: calendarTimedEventsDataProvider,
};

/**
 * `timeline[column].points` — the points mark of the vertical
 * `alias.chart` span-chart. Structurally analogous to `canvas.points`:
 * a first-class registered renderer whose DOM plugin delegates the
 * paint to the platform (here `LineChartBody.Renderer.Points`), rather
 * than the alias holding an inline kind→renderer switch.
 */
export const TIMELINE_COLUMN_POINTS_SPEC: PerTypeSpec = {
  id: "timeline[column].points",
  role: "renderer",
};

/**
 * `timeline[column].regression-curve` — the regression-curve mark of
 * the vertical `alias.chart` span-chart. First-class registered
 * renderer; its DOM plugin delegates the paint to
 * `LineChartBody.Renderer.RegressionCurve` (cf. `canvas.points`).
 */
export const TIMELINE_COLUMN_REGRESSION_CURVE_SPEC: PerTypeSpec = {
  id: "timeline[column].regression-curve",
  role: "renderer",
};

/**
 * `timeline[column].regression-band` — the regression-band mark of the
 * vertical `alias.chart` span-chart. First-class registered renderer;
 * its DOM plugin delegates the paint to
 * `LineChartBody.Renderer.RegressionBand` (cf. `canvas.points`).
 */
export const TIMELINE_COLUMN_REGRESSION_BAND_SPEC: PerTypeSpec = {
  id: "timeline[column].regression-band",
  role: "renderer",
};

/**
 * The `timeline[column].task` renderer's per-type spec, listed in `RENDERER_PER_TYPE_SPECS`; each
 * render front-end's plugin for that id implements it.
 */
export const TIMELINE_COLUMN_TASK_SPEC: PerTypeSpec = {
  id: "timeline[column].task",
  role: "renderer",
  accepts: [{ shape: RowShapes.TASK.key, onMismatch: "ignore" }],
  dataProvider: tasksDataProvider,
};

/**
 * The `timeline[column].timeline-legend` renderer's per-type spec, listed in
 * `RENDERER_PER_TYPE_SPECS`; each render front-end's plugin for that id implements it.
 */
export const TIMELINE_COLUMN_TIMELINE_LEGEND_SPEC: PerTypeSpec = {
  id: "timeline[column].timeline-legend",
  role: "renderer",
};

/**
 * All-day strip — filters to full-day events only. Timed events
 * paint on `timeline[column].event`.
 */
export const TIMELINE_ROW_EVENT_SPEC: PerTypeSpec = {
  id: "timeline[row].event",
  role: "renderer",
  accepts: [{ shape: RowShapes.EVENT.key, onMismatch: "ignore" }],
  dataProvider: calendarAllDayDataProvider,
};

/**
 * The `timeline[row].task` renderer's per-type spec, listed in `RENDERER_PER_TYPE_SPECS`; each
 * render front-end's plugin for that id implements it.
 */
export const TIMELINE_ROW_TASK_SPEC: PerTypeSpec = {
  id: "timeline[row].task",
  role: "renderer",
  accepts: [{ shape: RowShapes.TASK.key, onMismatch: "ignore" }],
  dataProvider: tasksDataProvider,
};

// ── The role lists ──────────────────────────────────────────────────

/**
 * Every built-in alias row's per-type spec, the list a render front-end walks to register one alias
 * plugin per spec.
 */
export const ALIAS_PER_TYPE_SPECS: readonly PerTypeSpec[] = deepFrozen([
  WEATHER_ALIAS_SPEC,
  TIMEZONE_ALIAS_SPEC,
  DATA_ALIAS_SPEC,
  CHART_ALIAS_SPEC,
  TASK_ALIAS_SPEC,
  TASK_LIST_ALIAS_SPEC,
  CALENDAR_ALIAS_SPEC,
  CALENDAR_LIST_ALIAS_SPEC,
  CALENDAR_ALLDAY_ALIAS_SPEC,
  STICKERS_ALIAS_SPEC,
]);

/**
 * Every built-in layout row's per-type spec, the list a render front-end walks to register one
 * layout plugin per spec.
 */
export const LAYOUT_PER_TYPE_SPECS: readonly PerTypeSpec[] = deepFrozen([
  TIMELINE_ROW_LAYOUT_SPEC,
  TIMELINE_COLUMN_LAYOUT_SPEC,
  CALENDAR_BODY_LAYOUT_SPEC,
  GRID_LAYOUT_SPEC,
  CANVAS_LAYOUT_SPEC,
  COLUMN_LAYOUT_SPEC,
]);

/**
 * Every built-in inert (display-only) row's per-type spec, the list a render front-end walks to
 * register one inert plugin per spec.
 */
export const INERT_PER_TYPE_SPECS: readonly PerTypeSpec[] = deepFrozen([
  BLOCK_INERT_SPEC,
  LABEL_INERT_SPEC,
  GROUP_INERT_SPEC,
  HEADER_INERT_SPEC,
  TIMELINE_LEGEND_INERT_SPEC,
  SHEET_FOOTER_INERT_SPEC,
  SURFACE_INERT_SPEC,
]);

/**
 * Every built-in renderer row's per-type spec, the list a render front-end walks to register one
 * renderer plugin per spec.
 */
export const RENDERER_PER_TYPE_SPECS: readonly PerTypeSpec[] = deepFrozen([
  // Base renderers (column.* + timeline-legend marker)
  COLUMN_TEXT_SPEC,
  COLUMN_NUMBER_SPEC,
  COLUMN_CHECKMARK_SPEC,
  COLUMN_GRAPH_SPEC,
  COLUMN_SERIES_SUMMARY_SPEC,
  COLUMN_DATA_JSON_SPEC,
  COLUMN_HEATMAP_SPEC,
  COLUMN_WEATHER_SPEC,
  COLUMN_TIMEZONE_SPEC,
  COLUMN_STICKER_SPEC,
  COLUMN_LOGS_SPEC,
  COLUMN_VIDEO_SPEC,
  COLUMN_HEADER_SECONDARY_SPEC,
  TIMELINE_COLUMN_TIMELINE_LEGEND_SPEC,
  // Task renderers
  COLUMN_TASK_SPEC,
  TIMELINE_ROW_TASK_SPEC,
  TIMELINE_COLUMN_TASK_SPEC,
  GRID_TASK_SPEC,
  // Calendar event renderers
  COLUMN_EVENT_LIST_SPEC,
  TIMELINE_ROW_EVENT_SPEC,
  TIMELINE_COLUMN_EVENT_SPEC,
  GRID_EVENT_SPEC,
  // Canvas renderers
  CANVAS_HRULE_SPEC,
  CANVAS_POINTS_SPEC,
  CANVAS_LINE_SPEC,
  CANVAS_BAR_SPEC,
  CANVAS_REGRESSION_CURVE_SPEC,
  CANVAS_REGRESSION_BAND_SPEC,
  CANVAS_REGRESSION_FILL_SPEC,
  CANVAS_CANDLESTICK_SPEC,
  CANVAS_FUNNEL_SPEC,
  // Chart marks — vertical `alias.chart` span-chart renderers
  TIMELINE_COLUMN_POINTS_SPEC,
  TIMELINE_COLUMN_REGRESSION_CURVE_SPEC,
  TIMELINE_COLUMN_REGRESSION_BAND_SPEC,
]);

/**
 * Every built-in per-type plugin spec, alias → layout → renderer, the
 * list the render-plugin registry is built from. Composed from the role-grouped sub-arrays
 * to keep `BUILTIN_RENDER_PLUGIN_SPECS` order stable across changes
 * (alias → layout → renderer is the same order the catalog used
 * before the per-type split).
 */
export const PER_TYPE_SPECS: readonly PerTypeSpec[] = deepFrozen([
  ...ALIAS_PER_TYPE_SPECS,
  ...LAYOUT_PER_TYPE_SPECS,
  ...INERT_PER_TYPE_SPECS,
  ...RENDERER_PER_TYPE_SPECS,
]);
